import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Briefcase, MapPin, Clock, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getAdminCareers, createCareer, updateCareer, deleteCareer } from '../../services/api';
import { toast } from 'sonner';

const CareersAdmin = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', department: '', location: '', type: 'Full-time',
    experience: '', description: '', requirements: '', email: '',
    is_active: true
  });

  const fetchData = async () => {
    try {
      const res = await getAdminCareers();
      setJobs(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: '', department: '', location: 'Ludhiana, Punjab', type: 'Full-time',
      experience: '', description: '', requirements: '', email: 'hr@namohtoolings.in',
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    setForm({
      title: job.title || '', department: job.department || '', location: job.location || '',
      type: job.type || 'Full-time', experience: job.experience || '',
      description: job.description || '', requirements: job.requirements || '',
      email: job.email || '', is_active: job.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateCareer(editing.id, form);
        toast.success('Job updated');
      } else {
        await createCareer(form);
        toast.success('Job posted');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job posting?')) return;
    try {
      await deleteCareer(id);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const active = jobs.filter(j => j.is_active).length;

  return (
    <div className="space-y-6" data-testid="careers-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Career Openings</h1>
          <p className="text-sm text-gray-500">{active} active openings</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-job-btn">
          <Plus size={16} className="mr-2" /> Post New Opening
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-6 h-24" /></Card>)}</div>
      ) : jobs.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500"><Briefcase className="mx-auto mb-3 text-gray-300" size={40} /><p>No job openings yet.</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {jobs.map(job => (
            <Card key={job.id} className={`hover:shadow-md transition-shadow ${!job.is_active ? 'opacity-60' : ''}`} data-testid={`job-card-${job.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-900">{job.title}</h3>
                      {!job.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {job.department && <span className="flex items-center gap-1"><Users size={12} /> {job.department}</span>}
                      {job.location && <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>}
                      {job.type && <span className="flex items-center gap-1"><Clock size={12} /> {job.type}</span>}
                      {job.experience && <span className="flex items-center gap-1"><Briefcase size={12} /> {job.experience}</span>}
                    </div>
                    <p className="text-xs text-gray-400">Apply to: {job.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(job)}><Pencil size={14} className="mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(job.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Post'} Job Opening</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Job Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="e.g. CNC Programmer" data-testid="job-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Production" /></div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Ludhiana, Punjab" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Job Type</Label>
                <select className="w-full h-9 rounded-md border border-input px-3 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
                </select>
              </div>
              <div><Label>Experience</Label><Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="e.g. 2-5 years" /></div>
            </div>
            <div><Label>Job Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={4} placeholder="Describe the role, responsibilities..." data-testid="job-description-input" /></div>
            <div><Label>Requirements</Label><Textarea value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} rows={3} placeholder="Skills, qualifications... (one per line)" /></div>
            <div><Label>Application Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="hr@namohtoolings.in" data-testid="job-email-input" /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Active (visible on website)</Label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="job-submit-btn">{editing ? 'Update' : 'Post'} Opening</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareersAdmin;
